import React, { useState, useEffect } from 'react';
import {
  Zap, Users, Clock, ArrowRight, CheckCircle,
} from 'lucide-react';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import TokenChecker from './TokenChecker';
import { error as logError } from "../utils/logger";
import ShaderBackground from './ShaderBackground';
import GradientDotsBackground from './GradientDotsBackground';

/* ─── Demo data ──────────────────────────────────────────────────────── */
const DEMO_EMAILS = [
  { from: "Sarah K.", subject: "Q3 budget review needed by Friday", signal: "High signal" },
  { from: "Ops Alerts", subject: "Build failed on mission-control", signal: "High signal" },
  { from: "Mike T.", subject: "Can you review the proposal draft?", signal: "Reply needed" },
  { from: "Hiring Digest", subject: "Weekly newsletter", signal: "Filtered" },
  { from: "Client Portal", subject: "Contract comment from legal", signal: "Assigned task" },
];
const SIGNAL_STYLES = {
  "High signal": "bg-[#10A7F3]/15 text-[#0D8ED3]",
  "Reply needed": "bg-[#10F3D6]/25 text-[#0b9e8d]",
  Filtered: "bg-gray-200 text-gray-500",
  "Assigned task": "bg-[#FFD8A8] text-[#9A5600]",
};
const EXTRACTION_STEPS = [
  "Extracting intent and deadlines",
  "Linking messages to existing projects",
  "Detecting ownership: personal vs assigned vs invited",
  "Drafting contextual reply suggestions",
];
const GENERATED_TASKS = [
  { title: "Review Q3 budget proposal", owner: "Personal", due: "Fri, 4:00 PM" },
  { title: "Fix mission-control CI failure", owner: "Assigned", due: "Today, 2:30 PM" },
  { title: "Join legal handoff for contract edits", owner: "Invited", due: "Tomorrow" },
];

/* ─── Cursor SVG ─────────────────────────────────────────────────────── */
const CursorSVG = ({ className = '' }) => (
  <svg className={className} width="18" height="22" viewBox="0 0 18 22" fill="none">
    <path
      d="M1 1L1 16L5.5 12L8.5 19L10.5 18.5L7.5 11.5L13 11.5L1 1Z"
      fill="#1e293b" stroke="white" strokeWidth="1.5" strokeLinejoin="round"
    />
  </svg>
);

/* ─── Animated FlowSync demo window ─────────────────────────────────── */
const FlowSyncDemo = () => {
  const [stage, setStage] = useState('idle');
  const [scanCount, setScanCount] = useState(0);
  const [clicking, setClicking] = useState(false);
  const [extractCount, setExtractCount] = useState(0);

  useEffect(() => {
    let mounted = true;
    const ids = [];

    const run = () => {
      if (!mounted) return;
      setStage('idle');
      setScanCount(0);
      setClicking(false);
      setExtractCount(0);

      ids.push(setTimeout(() => {
        if (!mounted) return;
        setClicking(true);

        ids.push(setTimeout(() => {
          if (!mounted) return;
          setClicking(false);
          setStage('ingesting');

          let count = 0;
          const tick = () => {
            if (!mounted) return;
            count++;
            setScanCount(count);
            if (count < 15) {
              ids.push(setTimeout(tick, 90));
            } else {
              ids.push(setTimeout(() => {
                if (!mounted) return;
                setStage('reasoning');
                let stepCount = 0;
                const stepTick = () => {
                  if (!mounted) return;
                  stepCount++;
                  setExtractCount(stepCount);
                  if (stepCount < EXTRACTION_STEPS.length) {
                    ids.push(setTimeout(stepTick, 600));
                  } else {
                    ids.push(setTimeout(() => {
                      if (!mounted) return;
                      setStage('orchestrating');
                      ids.push(setTimeout(() => {
                        if (!mounted) return;
                        setStage('complete');
                        ids.push(setTimeout(run, 5000));
                      }, 1500));
                    }, 500));
                  }
                };
                ids.push(setTimeout(stepTick, 500));
              }, 350));
            }
          };
          ids.push(setTimeout(tick, 120));
        }, 420));
      }, 1800));
    };

    ids.push(setTimeout(run, 700));
    return () => { mounted = false; ids.forEach(clearTimeout); };
  }, []);

  const visibleCount = Math.min(Math.ceil((scanCount / 15) * DEMO_EMAILS.length), DEMO_EMAILS.length);

  return (
    <div className="relative mx-auto w-full max-w-md">
      <div className="absolute inset-0 -z-10 rounded-3xl bg-[#10A7F3]/20 blur-3xl" />

      {/* window chrome */}
      <div className="overflow-hidden rounded-2xl border border-[#10A7F3]/30 bg-white shadow-2xl">

        {/* title bar */}
        <div className="flex items-center gap-2 border-b border-gray-200 bg-[#F5F7FA] px-4 py-3">
          <span className="h-3 w-3 rounded-full bg-[#FF5F57]/80" />
          <span className="h-3 w-3 rounded-full bg-[#FEBC2E]/80" />
          <span className="h-3 w-3 rounded-full bg-[#28C840]/80" />
          <div className="ml-3 flex-1 rounded-full bg-gray-200/70 px-3 py-1 text-center text-xs text-[#2A2D34]/60">
            {stage === 'idle' && "FlowSync ready"}
            {stage === 'ingesting' && "Ingesting Gmail and Outlook"}
            {stage === 'reasoning' && "Extracting context and ownership"}
            {stage === 'orchestrating' && "Routing into mission control"}
            {stage === 'complete' && "Execution pack generated"}
          </div>
        </div>

        <div className="relative min-h-[360px] p-4">

          {/* ── IDLE ── */}
          {stage === 'idle' && (
            <div className="flex flex-col h-full">
              <div className="space-y-1.5">
                {DEMO_EMAILS.map((email, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-[#F5F7FA] px-3 py-2 opacity-70">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-gray-200 text-xs font-semibold text-gray-500">
                      {email.from[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-[#1e293b]">{email.from}</div>
                      <div className="truncate text-xs text-[#2A2D34]/55">{email.subject}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="relative mt-4">
                <button
                  className={`w-full rounded-xl bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] py-3 text-sm font-semibold text-white shadow-md transition-transform duration-200 ${clicking ? 'scale-95' : 'scale-100'}`}
                >
                  Run FlowSync
                </button>
                {!clicking && (
                  <div className="pointer-events-none absolute inset-0 rounded-xl border-2 border-[#10A7F3]/50 animate-pulse" />
                )}
                <div className={`pointer-events-none absolute transition-all duration-500 ${
                  clicking
                    ? 'bottom-2 right-[86px] scale-75 opacity-75'
                    : 'bottom-5 right-[76px] scale-100 opacity-100'
                }`}>
                  <CursorSVG />
                </div>
              </div>
            </div>
          )}

          {stage === 'ingesting' && (
            <div className="space-y-3">
              <div>
                <div className="mb-1.5 flex items-center justify-between text-xs text-[#2A2D34]/65">
                  <span>Scanning communication channels</span>
                  <span className="font-semibold text-[#0D8ED3]">{scanCount} / 15</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] transition-all duration-100"
                    style={{ width: `${(scanCount / 15) * 100}%` }}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                {DEMO_EMAILS.slice(0, visibleCount).map((email, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg bg-[#F5F7FA] px-3 py-2">
                    <div className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-[#10A7F3]/10 text-xs font-semibold text-[#0D8ED3]">
                      {email.from[0]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-xs font-medium text-[#1e293b]">{email.from}</div>
                      <div className="truncate text-xs text-[#2A2D34]/60">{email.subject}</div>
                    </div>
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${SIGNAL_STYLES[email.signal]}`}>
                      {email.signal}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stage === 'reasoning' && (
            <div className="space-y-2.5">
              <div className="rounded-xl border border-[#10A7F3]/25 bg-[#10A7F3]/5 px-3 py-2">
                <div className="text-xs font-semibold text-[#0D8ED3]">Context engine</div>
                <div className="mt-1 text-xs text-[#2A2D34]/70">Turning messages into structured actions</div>
              </div>
              <div className="space-y-1.5">
                {EXTRACTION_STEPS.map((step, index) => {
                  const isDone = index < extractCount;
                  const isActive = index === extractCount;
                  return (
                    <div key={step} className="flex items-center gap-2 rounded-lg bg-[#F5F7FA] px-3 py-2">
                      <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold ${isDone ? 'bg-[#10A7F3] text-white' : isActive ? 'bg-[#10A7F3]/20 text-[#0D8ED3]' : 'bg-gray-200 text-gray-500'}`}>
                        {isDone ? "OK" : index + 1}
                      </span>
                      <span className={`text-xs ${isDone ? 'font-medium text-[#1e293b]' : 'text-[#2A2D34]/55'}`}>{step}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {stage === 'orchestrating' && (
            <div className="space-y-2">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-[#10A7F3]/10 py-2">
                  <div className="text-sm font-bold text-[#0D8ED3]">Personal</div>
                  <div className="text-[10px] text-[#2A2D34]/60">1 task</div>
                </div>
                <div className="rounded-lg bg-[#10F3D6]/10 py-2">
                  <div className="text-sm font-bold text-[#0a9e89]">Assigned</div>
                  <div className="text-[10px] text-[#2A2D34]/60">1 task</div>
                </div>
                <div className="rounded-lg bg-[#FFD8A8]/35 py-2">
                  <div className="text-sm font-bold text-[#9A5600]">Invited</div>
                  <div className="text-[10px] text-[#2A2D34]/60">1 task</div>
                </div>
              </div>
              <div className="space-y-1.5">
                {GENERATED_TASKS.map((task, i) => (
                  <div key={task.title} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs shadow-sm transition-all ${i === 0 ? 'border border-[#10A7F3]/25 bg-white' : 'bg-[#F5F7FA]'}`}>
                    <div className="min-w-0">
                      <div className="truncate font-medium text-[#1e293b]">{task.title}</div>
                      <div className="text-[10px] text-[#2A2D34]/55">{task.owner}</div>
                    </div>
                    <span className="ml-2 rounded-full bg-[#10A7F3]/10 px-2 py-0.5 text-[10px] font-semibold text-[#0D8ED3]">{task.due}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stage === 'complete' && (
            <div className="space-y-2.5">
              <div className="grid grid-cols-4 gap-1.5 text-center">
                <div className="rounded-lg bg-[#F5F7FA] py-2">
                  <div className="text-base font-bold text-[#1e293b]">15</div>
                  <div className="text-[10px] text-[#2A2D34]/60">Msgs</div>
                </div>
                <div className="rounded-lg bg-[#10A7F3]/10 py-2">
                  <div className="text-base font-bold text-[#0D8ED3]">3</div>
                  <div className="text-[10px] text-[#2A2D34]/60">Tasks</div>
                </div>
                <div className="rounded-lg bg-[#10F3D6]/10 py-2">
                  <div className="text-base font-bold text-[#0a9e89]">2</div>
                  <div className="text-[10px] text-[#2A2D34]/60">Links</div>
                </div>
                <div className="rounded-lg bg-[#FFD8A8]/35 py-2">
                  <div className="text-base font-bold text-[#9A5600]">1</div>
                  <div className="text-[10px] text-[#2A2D34]/60">Draft</div>
                </div>
              </div>
              <div className="rounded-xl border border-[#10A7F3]/20 bg-[#10A7F3]/5 p-2.5">
                <div className="mb-1.5 text-xs font-semibold text-[#0D8ED3]">Mission control tasks</div>
                {GENERATED_TASKS.map((task) => (
                  <div key={task.title} className="mb-1 last:mb-0 flex items-center justify-between rounded-lg bg-white px-2.5 py-1.5 text-xs shadow-sm">
                    <span className="truncate text-[#1e293b]">{task.title}</span>
                    <span className="ml-2 rounded-full bg-[#10A7F3]/15 px-2 py-0.5 text-[10px] font-semibold text-[#0D8ED3]">{task.owner}</span>
                  </div>
                ))}
              </div>
              <div className="rounded-xl border border-[#10F3D6]/25 bg-[#10F3D6]/5 p-2.5">
                <div className="mb-1 text-xs font-semibold text-[#0a9e89]">Suggested reply</div>
                <div className="rounded-lg bg-white px-2.5 py-2 text-xs shadow-sm">
                  <div className="text-[#2A2D34]/55">To: mike.t@company.com</div>
                  <div className="mt-0.5 font-medium text-[#1e293b]">Re: Proposal review timeline</div>
                  <div className="mt-1 line-clamp-2 text-[#2A2D34]/65">
                    Hi Mike - I linked this thread to Product Launch and created a task due Friday. I will share feedback before the handoff.
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

/* ─── LandingPage ────────────────────────────────────────────────────── */
const LandingPage = () => {
  const navigate = useNavigate();
  const useGradientDotsBackground = false;
  const BackgroundComponent = useGradientDotsBackground ? GradientDotsBackground : ShaderBackground;

  const handleLoginClick = async () => {
    const accessToken  = localStorage.getItem("accessToken");
    const refreshToken = localStorage.getItem("refreshToken");
    if (!accessToken || !refreshToken) { navigate("/login"); return; }
    try {
      const response = await TokenChecker("/api/token/check/", { method: "GET" });
      navigate(response.ok ? "/home" : "/login");
    } catch (err) {
      logError("Session check failed:", err);
      navigate("/login");
    }
  };

  const flowsyncSteps = [
    { n: "1", title: "Connect Gmail",          desc: "One-click authorization. We only access what's strictly needed."            },
    { n: "2", title: "AI scans & filters",     desc: "Important emails surfaced automatically. Noise removed."                    },
    { n: "3", title: "Tasks & replies ready",  desc: "Actions and drafted replies waiting in your workspace."                     },
  ];

  const features = [
    { icon: Zap,   title: "Lightning Fast",     description: "Streamline your workflow with intuitive tools designed for speed and efficiency."            },
    { icon: Users, title: "Team Synergy",        description: "Collaborate in real-time with your entire team, breaking down barriers and fostering innovation." },
    { icon: Clock, title: "Priority Management", description: "Focus on what matters most with advanced task prioritization and time management features."   },
  ];

  const stats = [
    { value: "10k+", label: "Tasks managed"   },
    { value: "3×",   label: "Faster triage"   },
    { value: "98%",  label: "Inbox clarity"   },
  ];

  return (
    <div className="relative flex flex-col min-h-screen overflow-x-hidden text-[#2A2D34]">

      {/* ── full-page animated wave background ── */}
      <div className="pointer-events-none fixed inset-0 -z-20">
        <BackgroundComponent />
      </div>

      {/* ══════════════════════ HEADER ══════════════════════════════════ */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between rounded-2xl border border-white/60 bg-white/70 px-6 py-4 shadow-sm backdrop-blur">
          <span className="cursor-pointer select-none text-3xl font-extrabold tracking-tight bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent md:text-4xl lg:text-5xl">
            Flowman
          </span>
          <div className="flex items-center gap-3">
            <span className="hidden md:block rounded-full border border-[#10A7F3]/30 bg-[#10A7F3]/10 px-3 py-1 text-sm font-medium text-[#0D8ED3]">
              New: Workspaces & Timelines
            </span>
            <button
              onClick={handleLoginClick}
              className="rounded-full border border-[#1e293b]/15 bg-white/80 px-5 py-2 text-sm font-semibold text-[#1e293b] backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-sm focus:outline-none"
            >
              Log in
            </button>
          </div>
        </div>
      </header>

      <main className="flex-grow">

        {/* ══════════════════════ HERO ════════════════════════════════ */}
        <motion.section
          className="relative flex min-h-[78vh] flex-col items-center justify-center px-4 text-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.1 }}
        >
          {/* soft white glow for readability over waves */}
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="h-[520px] w-[760px] rounded-full bg-white/55 blur-3xl" />
          </div>

          <div className="relative z-10 max-w-4xl">
            <div className="mx-auto mb-6 inline-flex items-center gap-2 rounded-full border border-[#10A7F3]/30 bg-white/80 px-4 py-1.5 text-sm font-medium text-[#0D8ED3] shadow-sm backdrop-blur">
              <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-[#10A7F3]" />
              AI-powered inbox intelligence
            </div>

            <h1 className="text-5xl font-extrabold leading-tight tracking-tight text-[#1e293b] md:text-6xl lg:text-7xl">
              Stop managing email.
              <br />
              Start shipping{" "}
              <span className="bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
                faster.
              </span>
            </h1>

            <p className="mx-auto mt-6 max-w-xl text-lg text-[#2A2D34]/70 md:text-xl">
              Flowman's AI reads your inbox, extracts tasks, and drafts replies — so your team acts on what matters, without the noise.
            </p>

            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <button
                onClick={() => navigate("/signup")}
                className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-[#10A7F3]/30 transition-all hover:-translate-y-0.5 hover:shadow-xl focus:outline-none"
              >
                Get started free
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <button
                onClick={handleLoginClick}
                className="inline-flex items-center justify-center rounded-full border border-[#1e293b]/15 bg-white/70 px-8 py-3.5 text-base font-semibold text-[#1e293b] backdrop-blur transition-all hover:-translate-y-0.5 hover:bg-white focus:outline-none"
              >
                Log in
              </button>
            </div>

            <div className="mt-7 flex flex-wrap items-center justify-center gap-6 text-sm text-[#2A2D34]/60">
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-[#10A7F3]" /> No credit card
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-[#10A7F3]" /> 2-minute setup
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle className="h-4 w-4 text-[#10A7F3]" /> Beta access
              </div>
            </div>
          </div>
        </motion.section>

        {/* ══════════════════════ STATS STRIP ════════════════════════ */}
        <div className="container mx-auto px-4 pb-20">
          <motion.div
            className="mx-auto grid max-w-3xl grid-cols-3 divide-x divide-[#10A7F3]/15 overflow-hidden rounded-2xl border border-white/60 bg-white/70 shadow-sm backdrop-blur"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            {stats.map((s) => (
              <div key={s.label} className="py-7 text-center">
                <div className="text-3xl font-extrabold bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
                  {s.value}
                </div>
                <div className="mt-1 text-sm text-[#2A2D34]/60">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        {/* ══════════════════════ FLOWSYNC SECTION ═══════════════════ */}
        <section className="container mx-auto px-4 pb-28">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#10A7F3]/30 bg-white/80 px-3 py-1 text-sm font-medium text-[#0D8ED3] shadow-sm backdrop-blur">
              <span className="inline-block h-2 w-2 rounded-full bg-[#10A7F3]" />
              AI-Powered Feature
            </div>
            <h2 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-[#1e293b] md:text-4xl">
              Meet{" "}
              <span className="bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] bg-clip-text text-transparent">
                FlowSync
              </span>
            </h2>
            <p className="mt-3 text-[#2A2D34]/70">
              Your Gmail inbox, turned into action. FlowSync uses AI to surface what matters and skip the rest.
            </p>
          </div>

          <motion.div
            className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-10 md:grid-cols-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9 }}
          >
            {/* left: steps glass card */}
            <div className="relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 p-8 shadow-xl backdrop-blur">
              <div className="absolute -right-8 -top-8 h-32 w-32 rounded-full bg-[#10A7F3]/10 blur-2xl" />

              <div className="space-y-4">
                {flowsyncSteps.map((step) => (
                  <div key={step.n} className="flex items-start gap-4 rounded-xl bg-[#F5F7FA]/80 p-4">
                    <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#10A7F3] to-[#10F3D6] text-sm font-bold text-white shadow-sm">
                      {step.n}
                    </div>
                    <div>
                      <div className="font-semibold text-[#1e293b]">{step.title}</div>
                      <div className="mt-0.5 text-sm text-[#2A2D34]/65">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={() => navigate("/signup")}
                className="mt-8 inline-flex w-full items-center justify-center rounded-full bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-6 py-3 font-semibold text-white shadow-lg shadow-[#10A7F3]/25 transition-all hover:-translate-y-0.5 hover:shadow-xl focus:outline-none"
              >
                Try FlowSync Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </button>
              <p className="mt-3 text-center text-xs text-[#2A2D34]/40">Beta access required · No credit card needed</p>
            </div>

            {/* right: animated demo */}
            <FlowSyncDemo />
          </motion.div>
        </section>

        {/* ══════════════════════ FEATURE HIGHLIGHTS ══════════════════ */}
        <section className="container mx-auto px-4 pb-28">
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <h2 className="text-3xl font-extrabold tracking-tight text-[#1e293b] md:text-4xl">
              Built for how modern teams work
            </h2>
            <p className="mt-3 text-[#2A2D34]/70">
              Tools that stay out of your way — so your team can focus on shipping.
            </p>
          </div>
          <div className="mx-auto grid max-w-6xl grid-cols-1 gap-6 md:grid-cols-3">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                className="group relative overflow-hidden rounded-2xl border border-white/60 bg-white/80 p-8 shadow backdrop-blur transition-all hover:shadow-lg"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.15 }}
              >
                <div className="absolute -right-6 -top-6 h-24 w-24 rounded-full bg-[#10A7F3]/10 blur-xl transition-all group-hover:scale-110" />
                <feature.icon className="mb-6 h-12 w-12 text-[#10A7F3]" />
                <h3 className="text-xl font-bold text-[#1e293b]">{feature.title}</h3>
                <p className="mt-2 leading-relaxed text-[#2A2D34]/70">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ══════════════════════ CTA BANNER ══════════════════════════ */}
        <section className="container mx-auto px-4 pb-24">
          <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-gradient-to-r from-[#10A7F3] to-[#10F3D6] px-8 py-16 text-center text-white shadow-xl shadow-[#10A7F3]/20">
            <div
              className="pointer-events-none absolute inset-0 opacity-20"
              style={{ backgroundImage: "radial-gradient(circle at 20% 50%, white 0%, transparent 50%), radial-gradient(circle at 80% 20%, white 0%, transparent 40%)" }}
            />
            <h2 className="relative text-3xl font-extrabold tracking-tight md:text-4xl">
              Ready to reclaim your inbox?
            </h2>
            <p className="relative mx-auto mt-4 max-w-xl text-white/80 md:text-lg">
              Join the teams using Flowman to turn email chaos into structured, actionable work.
            </p>
            <button
              onClick={() => navigate("/signup")}
              className="relative mt-8 inline-flex items-center justify-center rounded-full bg-white px-8 py-3.5 text-base font-semibold text-[#0D8ED3] shadow-lg transition-all hover:-translate-y-0.5 hover:shadow-xl focus:outline-none"
            >
              Get started free
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
            <div className="relative mt-4 text-sm text-white/60">No credit card · 2-minute setup · Beta access</div>
          </div>
        </section>

      </main>

      {/* ══════════════════════ FOOTER ══════════════════════════════════ */}
      <footer className="mt-auto bg-[#2A2D34] text-white">
        <div className="h-px w-full bg-gradient-to-r from-transparent via-white/20 to-transparent" />
        <div className="container mx-auto flex flex-col items-center justify-between px-4 py-10 md:flex-row">
          <div className="mb-4 text-sm md:mb-0">&copy; {new Date().getFullYear()} Flowman. All rights reserved.</div>
          <div className="flex space-x-6 text-sm">
            <a href="/privacy" className="hover:text-[#10A7F3] transition-colors">Privacy Policy</a>
            <a href="/terms"   className="hover:text-[#10A7F3] transition-colors">Terms of Service</a>
            <a href="/contact" className="hover:text-[#10A7F3] transition-colors">Contact Us</a>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
